import { Button, Form, Input, Select, Switch, Typography } from "antd";
import CustomSelect from "components/CustomSelect";
import useAPIResponse from "hooks/useAPIResponse";
import React, { useEffect, useMemo } from "react";
import SavedSearchAPI from "restAPI/SavedSearchAPI";
import { ComboChartIndividualChartTypeOptionValue, ComboChartIndividualChartTypeOptions, TIME_LINE_WIDGET_CHART_TYPE, parseSavedSearchOptions } from "views/Dashboards/CustomDashboardConstants";
import ACColorPicker from "ac-components/ac-color-picker/ACColorPicker";
import DeleteButton from "components/DeleteButton";
import { PlusIcon } from "components/Icons";
import { filter, find, includes } from "lodash";
import { getUrlSearch, parseURLSearch } from "utils/urlUtil";
import { useFeatureFlag } from "contexts/FeatureFlagContext";

export const PLACEHOLDER_MAP = {
    findings: "Finding View Filter",
    assets: "Asset View Filter",
    tickets: "Saved Search"
}

export default function ComboChartSelectionListField({
  value,
  form,
  savedSearchType = "findings",
  onlyTimeSeries,
  setSavedSearchList = () => {},
  redirectionTo = "findings",
}) {
  const featureFlags = useFeatureFlag();
  const savedSearchNameToDisplay = PLACEHOLDER_MAP[savedSearchType];
  const [savedSearchList, savedSearchLoading] = useAPIResponse(
    () => {
      const request = SavedSearchAPI.getAllSavedFilter(savedSearchType);
      if(onlyTimeSeries){
        request.promise = request.promise.then((list) =>
          list.filter((item) => item.storeTimeSeries)
        );
      }
      return request;
    },
    []
  );
  useEffect(() => {
    setSavedSearchList(savedSearchList);
  }, [savedSearchList, setSavedSearchList])

  const chartType = form.getFieldValue("chartType");

  const typeOptions = useMemo(() => {
    if(!chartType){
      return ComboChartIndividualChartTypeOptions;
    }
    if(chartType.includes("BAR")){
      return filter(ComboChartIndividualChartTypeOptions, {
        value: ComboChartIndividualChartTypeOptionValue.BAR,
      });
    } else if (chartType.includes("LINE") || chartType.includes("AREA")){
      return filter(ComboChartIndividualChartTypeOptions, {
        value: ComboChartIndividualChartTypeOptionValue.LINE,
      });
    }
  }, [chartType]);

  const isStack = includes(chartType, "STACK");
  return (
    <div className="combo-chart-selection-field">
      <div className="headers">
        <div className="segment required-mark">
          X-axis buckets/segment
        </div>
        <div className="type">Type</div>
        <div className="color">Color</div>
        <div className="right-axis">Right Axis</div>
        <div className="action" />
      </div>
      <Form.List name="charts">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, i) => {
              const selectedSavedSearch = find(savedSearchList, {
                id: value?.[i]?.search,
              });
              return (
                <>
                <div className="list-row" key={field.key}>
                  <div className="segment">
                    <Form.Item
                      name={[field.name, "search"]}
                      rules={[
                        { required: true, message: "Select a " + savedSearchNameToDisplay},
                      ]}
                    >
                      <CustomSelect
                        options={savedSearchList}
                        loading={savedSearchLoading}
                        allowClear
                        optionsMutator={(o) => parseSavedSearchOptions(o, {disableAQLv2: featureFlags.FINDING_AQL_V2})}
                        placeholder= {savedSearchNameToDisplay}
                        showSearch={true}
                        optionFilterProp="name"
                      />
                    </Form.Item>
                  </div>
                  <div className="preview-link">
                    <Typography.Link
                      disabled={!selectedSavedSearch}
                      target="_blank"
                      href={
                        selectedSavedSearch
                          ? `/#/${redirectionTo}?${getUrlSearch({
                              ...parseURLSearch(
                                selectedSavedSearch.filters
                              ),
                              savedSearch: { id: selectedSavedSearch.id },
                            })}`
                          : undefined
                      }
                    >
                      Preview Data
                    </Typography.Link>
                  </div>
                  <div className="type">
                    <Form.Item name={[field.name, "type"]}>
                      <Select options={typeOptions} />
                    </Form.Item>
                  </div>
                  <div className="color">
                    <Form.Item name={[field.name, "color"]}>
                      <ACColorPicker />
                    </Form.Item>
                  </div>
                  <div className="right-axis">
                    <Form.Item
                      name={[field.name, "axis"]}
                      valuePropName="checked"
                    >
                      <Switch size="small" disabled={isStack} />
                    </Form.Item>
                  </div>
                  <div className="action">
                    <DeleteButton
                      disabled={fields.length < 2}
                      skipConfirm
                      onDelete={() => remove(field.name)}
                    />
                  </div>
                </div>
                <div style={{ width: "280px" }}>
                  <Form.Item name={[field.name, "customLabel"]}>
                    <Input placeholder={"Custom label for " + savedSearchNameToDisplay} />
                  </Form.Item>
                </div>
                </>
              );
            })}
            <Button
              onClick={() =>
                add({
                  ...ComboChartSelectionListField.getDefaultValueObject(),
                  type: typeOptions[0].value,
                })
              }
            >
              <PlusIcon className="m-r-xs" /> Add {savedSearchNameToDisplay}
            </Button>
          </>
        )}
      </Form.List>
    </div>
  );
}

ComboChartSelectionListField.getDefaultValueObject = () => {
  return { color: "#FF0000", type: TIME_LINE_WIDGET_CHART_TYPE.LINE };
};
